import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Segment from "components/base/Segment";
import { InlineSpinner } from "components/base/Spinner";
import { useModals } from "ModalProvider";
import {
  FETCH_ALC_B2C_STOCK,
  FETCH_ALC_INCOMING_XML_FILES,
  FETCH_SHOPIFY_ALC_ORDERS,
} from "./graphql";
import CsvPreviewModal from "./CsvPreviewModal";
import XmlPreviewModal from "./XmlPreviewModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const getRecordCountText = (total, shown) =>
  shown === total ? `${total} records` : `Showing ${shown} of ${total} records`;

const RecordCount = ({ total, shown }) => (
  <div className="mb-2 text-right text-xs opacity-60">
    {getRecordCountText(total, shown)}
  </div>
);

const SHOPIFY_SOURCES = [
  { label: "AB", value: "AB" },
  { label: "INC", value: "INC" },
];

const ShopifyAlcOrdersPage = () => {
  const [shopifySearch, setShopifySearch] = useState("");
  const [shopifySource, setShopifySource] = useState("AB");
  const [alcSearch, setAlcSearch] = useState("");
  const [xmlSearch, setXmlSearch] = useState("");
  const csvModal = useModals({ center: true, maxWidth: "max-w-7xl" });
  const xmlModal = useModals({ center: true, maxWidth: "max-w-6xl" });
  const {
    data: alcData,
    loading: alcLoading,
    error: alcError,
    refetch: refetchAlc,
  } = useQuery(FETCH_ALC_B2C_STOCK, {
    fetchPolicy: "network-only",
  });
  const {
    data: shopifyData,
    loading: shopifyLoading,
    error: shopifyError,
    refetch: refetchShopify,
  } = useQuery(FETCH_SHOPIFY_ALC_ORDERS, {
    variables: { source: shopifySource },
    fetchPolicy: "network-only",
  });
  const {
    data: xmlData,
    loading: xmlLoading,
    error: xmlError,
    refetch: refetchXml,
  } = useQuery(FETCH_ALC_INCOMING_XML_FILES, {
    fetchPolicy: "network-only",
  });

  const orders = shopifyData?.shopifyShopOrders || [];
  const latestShopifyOrder = shopifyData?.latestShopifyOrder;
  const xmlFiles = xmlData?.alcIncomingXmlFiles || [];
  const alcItems = useMemo(() => {
    if (!alcData?.alcB2cStock) return [];
    try {
      const parsed = JSON.parse(alcData.alcB2cStock);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Failed to parse alcB2cStock", e);
      return [];
    }
  }, [alcData?.alcB2cStock]);

  const alcKeyword = alcSearch.trim().toLowerCase();
  const shopifyKeyword = shopifySearch.trim().toLowerCase();
  const xmlKeyword = xmlSearch.trim().toLowerCase();

  const filteredAlcItems = useMemo(() => {
    if (!alcKeyword) return alcItems;
    return alcItems.filter((item) => {
      return (
        item.number?.toLowerCase().includes(alcKeyword) ||
        item.name?.toLowerCase().includes(alcKeyword)
      );
    });
  }, [alcItems, alcKeyword]);

  const filteredShopifyOrders = useMemo(() => {
    if (!shopifyKeyword) return orders;
    return orders.filter((order) => {
      return (
        order.name?.toLowerCase().includes(shopifyKeyword) ||
        order.soName?.toLowerCase().includes(shopifyKeyword) ||
        order.csv?.toLowerCase().includes(shopifyKeyword)
      );
    });
  }, [orders, shopifyKeyword]);

  const filteredXmlFiles = useMemo(() => {
    if (!xmlKeyword) return xmlFiles;
    return xmlFiles.filter((file) => {
      const createdAtText = formatDateTime(file.createdAt).toLowerCase();
      return (
        file.name?.toLowerCase().includes(xmlKeyword) ||
        createdAtText.includes(xmlKeyword)
      );
    });
  }, [xmlFiles, xmlKeyword]);

  const openCsvPreview = (csvFileName) => {
    if (!csvFileName) return;
    csvModal.present({
      title: "CSV Preview",
      subtitle: csvFileName,
      children: (
        <CsvPreviewModal csvFileName={csvFileName} hide={csvModal.hide} />
      ),
    });
  };

  const openXmlPreview = (xmlFileName) => {
    if (!xmlFileName) return;
    xmlModal.present({
      title: "ALC XML Preview",
      subtitle: xmlFileName,
      center: true,
      children: (
        <XmlPreviewModal xmlFileName={xmlFileName} hide={xmlModal.hide} />
      ),
    });
  };

  return (
    <Page
      title="Shopify & ALC"
      subtitle={
        <>
          Daily Shopify-to-ALC order sync and ALC shipment sync{" "}
          <strong>run at 08:00 China time / 02:00 Europe time</strong>.
        </>
      }
      childrenClassName="p-6 overflow-auto"
      backTo="./.."
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-base font-semibold">
              Shopify Orders(WIS handled)
            </div>
            <div className="flex shrink-0 items-center gap-3 whitespace-nowrap">
              <Segment
                options={SHOPIFY_SOURCES}
                value={shopifySource}
                onChange={setShopifySource}
              />
              <Button
                primary
                refresh
                loading={shopifyLoading}
                onClick={() => refetchShopify()}
              >
                Refresh
              </Button>
            </div>
          </div>
          <div className="mb-4">
            <input
              value={shopifySearch}
              onChange={(e) => setShopifySearch(e.target.value)}
              placeholder="Search: order / SO / CSV"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
            />
          </div>
          <div className="mb-2 flex items-center justify-between gap-3 text-xs">
            <span className="opacity-60">
              Shopify latest:{" "}
              {latestShopifyOrder?.adminUrl ? (
                <a
                  href={latestShopifyOrder.adminUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky-600 hover:text-sky-800"
                >
                  {latestShopifyOrder.name}
                </a>
              ) : (
                "-"
              )}
            </span>
            <span className="opacity-60">
              {getRecordCountText(orders.length, filteredShopifyOrders.length)}
            </span>
          </div>

          {shopifyLoading ? (
            <div className="py-8 flex justify-center">
              <InlineSpinner text="Loading Shopify orders..." />
            </div>
          ) : shopifyError ? (
            <Errors error={shopifyError} className="py-6" />
          ) : filteredShopifyOrders.length === 0 ? (
            <div className="text-sm opacity-70 py-8 text-center">No matching data</div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="table with-line w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left">Shopify</th>
                    <th className="text-left">To ALC</th>
                    <th className="text-left">Odoo SO</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShopifyOrders.map((order) => {
                    return (
                      <tr key={order.id}>
                        <td>
                          {order.adminUrl ? (
                            <a
                              href={order.adminUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sky-600 hover:text-sky-800"
                            >
                              {order.name || "-"}
                            </a>
                          ) : (
                            order.name || "-"
                          )}
                        </td>
                        <td className="font-mono text-xs">
                          {order.csv ? (
                            <button
                              type="button"
                              className="text-sky-600 hover:text-sky-800 hover:underline"
                              onClick={() => openCsvPreview(order.csv)}
                            >
                              {order.csv}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{order.soName || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-base font-semibold">
              ALC Shipments
            </div>
            <Button
              primary
              refresh
              loading={xmlLoading}
              onClick={() => refetchXml()}
            >
              Refresh
            </Button>
          </div>
          <div className="mb-4">
            <input
              value={xmlSearch}
              onChange={(e) => setXmlSearch(e.target.value)}
              placeholder="Search: XML file name"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
            />
          </div>
          <RecordCount total={xmlFiles.length} shown={filteredXmlFiles.length} />

          {xmlLoading ? (
            <div className="py-8 flex justify-center">
              <InlineSpinner text="Loading XML files..." />
            </div>
          ) : xmlError ? (
            <Errors error={xmlError} className="py-6" />
          ) : filteredXmlFiles.length === 0 ? (
            <div className="text-sm opacity-70 py-8 text-center">No matching data</div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="table with-line w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left">XML File</th>
                    <th className="text-left">Created(ctime)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredXmlFiles.map((file) => (
                    <tr key={file.name}>
                      <td className="font-mono text-xs">
                        <button
                          type="button"
                          className="text-sky-600 hover:text-sky-800 hover:underline"
                          onClick={() => openXmlPreview(file.name)}
                        >
                          {file.name}
                        </button>
                      </td>
                      <td className="text-xs whitespace-nowrap">
                        {formatDateTime(file.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-base font-semibold">
              ALC Product & Stock
            </div>
            <Button
              primary
              refresh
              loading={alcLoading}
              onClick={() => refetchAlc()}
            >
              Refresh
            </Button>
          </div>
          <div className="mb-4">
            <input
              value={alcSearch}
              onChange={(e) => setAlcSearch(e.target.value)}
              placeholder="Search: item number / name"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
            />
          </div>
          <RecordCount total={alcItems.length} shown={filteredAlcItems.length} />

          {alcLoading ? (
            <div className="py-8 flex justify-center">
              <InlineSpinner text="Loading ALC data..." />
            </div>
          ) : alcError ? (
            <Errors error={alcError} className="py-6" />
          ) : filteredAlcItems.length === 0 ? (
            <div className="text-sm opacity-70 py-8 text-center">No matching data</div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="table with-line w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left">Item</th>
                    <th className="text-left">Name</th>
                    <th className="text-left">Available</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlcItems.map((item) => (
                    <tr key={item.number}>
                      <td className="font-mono">{item.number}</td>
                      <td>{item.name || "-"}</td>
                      <td>{item.available_qty ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </Page>
  );
};

export default ShopifyAlcOrdersPage;
